import { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Lock, Check, ChevronRight } from "lucide-react";
import { Chronicle, ERAS } from "@/data/chronicles";
import { getChronicleChains, unbuiltMembersOfChain } from "@/data/chronicle-chains";
import { getUnlockRule, isChronicleUnlocked } from "@/engine/chronicleUnlockStore";
// Ids only: the home map counts progress; the full ledger text loads with the Ledger.
import { chronicleMemoryFlagIds } from "@/data/chronicle-memory-flag-ids";
import { getUnlockedFlags } from "@/engine/chronicleMemoryStore";
import { SAVE_KEYS } from "@/hooks/useLastPlayedChronicle";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { TUTORIAL_HOME_VIEW_EVENT, isWalkthroughEvent } from "@/components/tutorial/tutorial-session";

// chronicleId → engine save key, derived from the canonical SAVE_KEYS
// table. Used to detect in-progress chronicles for the Continue pill.
const SAVE_KEY_BY_CHRONICLE: Record<string, string> = SAVE_KEYS.reduce(
  (acc, { chronicleId, key }) => {
    acc[chronicleId] = key;
    return acc;
  },
  {} as Record<string, string>,
);

// Layer 1 — chronicle-chain UI. The chain registry + helpers now live
// in `src/data/chronicle-chains.ts` (shared with `ArchivistLedger.tsx`
// where the same chains are surfaced as filter chips). This component
// only consumes them via `getChronicleChains()` +
// `unbuiltMembersOfChain()` from the import above; see the data file
// for the chain definitions and scope rules.

/** True if the chronicle has an in-progress (not yet complete) save
 *  AND the player has actually made at least one choice. The Play
 *  page autosaves the initial GameState on first load, so without
 *  the choice-history check a save would exist for any chronicle
 *  the player merely OPENED — including immediately after a reset,
 *  which is exactly when the Continue affordance should not appear. */
function chronicleHasInProgressSave(chronicleId: string): boolean {
  const key = SAVE_KEY_BY_CHRONICLE[chronicleId];
  if (!key) return false;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (parsed?.isComplete === true) return false;
    const decisions = Array.isArray(parsed?.choiceHistory)
      ? parsed.choiceHistory.length
      : 0;
    return decisions > 0;
  } catch { return false; }
}

/** Count of (endings reached, total endings) for a chronicle. Reads
 *  from the chronicle-memory store and the memory-flag definition.
 *  Used by the dropdown to show a "3/7 endings" chip on completed-
 *  but-not-fully-explored chronicles, hinting that replays surface
 *  more. */
function chronicleEndingsProgress(chronicleId: string): { reached: number; total: number } {
  const ids = chronicleMemoryFlagIds[chronicleId] || [];
  const endingIds = ids.filter((id) => id.startsWith("ending_"));
  const total = endingIds.length;
  if (total === 0) return { reached: 0, total: 0 };
  const unlocked = new Set(getUnlockedFlags(chronicleId));
  const reached = endingIds.filter((id) => unlocked.has(id)).length;
  return { reached, total };
}

/** Group chronicles by ERA. Chronicles without an era match (e.g. their
 *  start year falls outside any defined era) end up in "Other" and
 *  render at the bottom. Era ordering follows ERAS' declaration order. */
function groupChroniclesByEra(chronicles: Chronicle[]): Array<{ eraLabel: string; chronicles: Chronicle[] }> {
  const groups: Map<string, Chronicle[]> = new Map();
  for (const era of ERAS) groups.set(era.label, []);
  groups.set("Other", []);
  for (const c of chronicles) {
    const era = ERAS.find((e) => c.dateRange[0] >= e.start && c.dateRange[0] < e.end);
    const label = era ? era.label : "Other";
    groups.get(label)!.push(c);
  }
  // Within each era, sort by start year so the chronological order
  // reads naturally (Burgundian → Dutch Revolt → VOC → Belgian Revolution).
  for (const [, list] of groups) list.sort((a, b) => a.dateRange[0] - b.dateRange[0]);
  return [...groups.entries()]
    .filter(([, list]) => list.length > 0)
    .map(([eraLabel, chronicles]) => ({ eraLabel, chronicles }));
}

/**
 * Region emoji glyphs — the original colour icons, restored at the user's
 * request. Color emoji glyphs render in their native palette (the lion,
 * crown, fleur-de-lis etc. each carry their own colour) which the user
 * prefers over the gold-engraved SVG heraldry approach.
 */
const REGION_ICONS: Record<string, string> = {
  lowCountries: "🦁",
  britishIsles: "👑",
  hre: "🦅",
  iberia: "⚔️",
  france: "⚜️",
  italia: "🏛️",
  scandinavia: "ᚱ",
  easternEurope: "🐻",
  balkans: "🏰",
};

interface RegionMarkerProps {
  regionId: string;
  regionName: string;
  chronicles: Chronicle[];
  /**
   * Fired when the marker's own pointer state changes. Index.tsx lifts this
   * to drive the timeline's matching-region highlight. Undefined disables
   * the propagation (e.g. on touch devices).
   */
  onHoverChange?: (regionId: string | null) => void;
}

interface PanelPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
  transformOrigin: string;
  maxHeight: number;
}

/** Desktop dropdown — viewport-aware */
function DesktopPanel({
  regionId,
  regionName,
  chronicles,
  panelRef,
  dropdownRef,
  onClose,
}: {
  regionId: string;
  regionName: string;
  chronicles: Chronicle[];
  panelRef: React.RefObject<HTMLDivElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
}) {
  useNavigate();
  const [completedChronicles, setCompletedChronicles] = useState<string[]>([]);
  const [panelPos, setPanelPos] = useState<PanelPosition>({
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    transformOrigin: "top center",
    maxHeight: 400,
  });

  useEffect(() => {
    try { setCompletedChronicles(JSON.parse(localStorage.getItem("chronicles-completed") || "[]")); } catch { setCompletedChronicles([]); }
  }, []);

  const computePosition = useCallback(() => {
    if (!panelRef.current) return;
    const markerRect = panelRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const panelW = Math.min(256, vw - 24);
    const margin = 12;
    // Reserve space for the top-right GlobalControls cluster
    const controlsReserveW = 110;
    const controlsReserveH = 56;
    // Reserve space for the top banner / header so a panel opening
    // upward never slides behind it.
    const topBannerReserve = 72;

    // Vertical: pick the side with strictly more available space when
    // neither side has comfortable headroom.
    const spaceBelow = Math.max(0, vh - markerRect.bottom - margin);
    const spaceAbove = Math.max(0, markerRect.top - margin - topBannerReserve);
    const COMFORTABLE = 240;
    let openBelow: boolean;
    if (spaceBelow >= COMFORTABLE) openBelow = true;
    else if (spaceAbove >= COMFORTABLE) openBelow = false;
    else openBelow = spaceBelow >= spaceAbove;
    // maxH is the actual available space on the chosen side, capped at
    // 400. No artificial floor — the panel scrolls internally rather
    // than spilling off-screen / behind the header.
    const available = openBelow ? spaceBelow : spaceAbove;
    const maxH = Math.max(140, Math.min(400, available || 140));

    // Horizontal: clamp panel inside [margin, vw - margin - panelW]. Reserve
    // top-right controls only when the panel is high up AND there's room to
    // shift left without going off-screen left — otherwise honour the
    // viewport edge over the controls.
    const markerCenterX = markerRect.left + markerRect.width / 2;
    let leftOffset = markerCenterX - panelW / 2;
    const nearTop = markerRect.top < controlsReserveH;
    const hardRight = vw - margin - panelW;
    const softRight = nearTop ? Math.min(hardRight, vw - margin - controlsReserveW - panelW) : hardRight;
    // If softRight would push left of margin, fall back to hardRight (viewport > controls).
    const rightBound = softRight < margin ? hardRight : softRight;
    leftOffset = Math.max(margin, Math.min(leftOffset, rightBound));
    const relLeft = leftOffset - markerRect.left;

    const pos: PanelPosition = { transformOrigin: openBelow ? "top center" : "bottom center", maxHeight: maxH };
    if (openBelow) pos.top = "100%"; else pos.bottom = "100%";
    pos.left = `${relLeft}px`;
    setPanelPos(pos);
  }, [panelRef]);

  useEffect(() => {
    computePosition();
    window.addEventListener("resize", computePosition);
    // Recompute on scroll too (capture phase to catch nested scrollers like
    // the map container when the user pans). Without this, the panel can
    // anchor against a stale marker position and clip off-screen.
    window.addEventListener("scroll", computePosition, true);
    return () => {
      window.removeEventListener("resize", computePosition);
      window.removeEventListener("scroll", computePosition, true);
    };
  }, [computePosition]);

  return (
    <motion.div
      ref={dropdownRef}
      initial={{ opacity: 0, scaleY: 0.3 }}
      animate={{ opacity: 1, scaleY: 1 }}
      exit={{ opacity: 0, scaleY: 0.3 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="absolute w-64 max-w-[calc(100vw-24px)]"
      style={{
        top: panelPos.top,
        bottom: panelPos.bottom,
        left: panelPos.left,
        right: panelPos.right,
        transform: panelPos.transform,
        transformOrigin: panelPos.transformOrigin,
        marginTop: panelPos.top ? "12px" : undefined,
        marginBottom: panelPos.bottom ? "12px" : undefined,
      }}
      // Tablet fix: this panel renders INSIDE the EuropeMap container,
      // whose onTouchStart/Move pans the map. Without stopPropagation
      // every finger drag inside the chronicle list would also pan the
      // map behind. We swallow the touch events here so only the list
      // reacts. (Click events still bubble normally for the buttons.)
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div className="h-[2px]" style={{ background: "hsl(var(--gold-dim) / 0.25)" }} />
      {/* Card wrapper holds the scrollable list AND a fixed Close footer.
          Background / shadow / side-borders live here (not on the scroller)
          so the footer shares the card surface. */}
      <div
        style={{
          background: "hsl(var(--card))",
          boxShadow: "0 12px 40px hsl(var(--ink) / 0.6), 0 4px 12px hsl(var(--ink) / 0.3)",
          border: "1px solid hsl(var(--border))",
          borderTop: "none",
          borderBottom: "none",
        }}
      >
        <div
          className="relative overflow-y-auto"
          style={{
            // Reserve ~52px for the always-visible Close footer below so the
            // whole panel still fits the computed available space.
            maxHeight: `${Math.max(72, panelPos.maxHeight - 52)}px`,
            // Restore vertical scrolling for touch users. The parent
            // EuropeMap container has `touch-action: none`, which would
            // otherwise inherit and block finger-scrolling this list on
            // tablets.
            touchAction: "pan-y",
          }}
        >
          <PanelContent regionId={regionId} regionName={regionName} chronicles={chronicles} completedChronicles={completedChronicles} onClose={onClose} />
        </div>
        {/* Close button — a fixed footer OUTSIDE the scroll area, so it is
            ALWAYS visible for every region regardless of how long the
            chronicle list is. (The previous sticky-inside-scroll approach
            didn't reliably show on longer lists — the cause of "not all
            dropdowns have a Close.") Mirrors the mobile bottom-sheet's Close
            so every region menu dismisses the same way. The dropdown also
            still closes on outside-click; this is the explicit affordance. */}
        <div
          className="px-3 pb-3 pt-2"
          style={{ borderTop: "1px solid hsl(var(--border) / 0.2)" }}
        >
          <button
            onClick={onClose}
            className="w-full py-2 font-display text-[11px] uppercase tracking-[0.2em] text-muted-foreground border border-border/40 rounded-sm hover:text-foreground transition-colors"
          >
            Close
          </button>
        </div>
      </div>
      <div className="h-[2px]" style={{ background: "hsl(var(--gold-dim) / 0.25)" }} />
    </motion.div>
  );
}

/** Shared panel content. Three improvements landed 2026-05:
 *   1. Chronicles are grouped by era under quiet Cinzel-caps headers,
 *      so a region with 6+ chronicles spanning four centuries is
 *      scannable at a glance.
 *   2. In-progress saves get a small "Continue" pill so the player
 *      can resume directly from the region menu instead of going
 *      back to the home screen.
 *   3. Completed chronicles show an "X / Y endings" chip pulling
 *      from the chronicle-memory store, hinting at replay value
 *      without opening the chronicle. */
function PanelContent({
  regionId, regionName, chronicles, completedChronicles, onClose,
}: {
  regionId: string;
  regionName: string;
  chronicles: Chronicle[];
  completedChronicles: string[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const groups = useMemo(() => groupChroniclesByEra(chronicles), [chronicles]);

  return (
    <>
      <div className="relative px-4 pt-4 pb-2" data-tutorial="region-panel-intro">
        <div className="flex items-center gap-2.5">
          <span className="text-lg opacity-70">{REGION_ICONS[regionId] || "📍"}</span>
          <div>
            <h3 className="font-display text-sm text-foreground tracking-[0.08em]">{regionName}</h3>
            <p className="text-[10px] text-muted-foreground font-display uppercase tracking-[0.12em] mt-0.5">
              {chronicles.length} chronicle{chronicles.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="mt-2.5 h-px bg-border" />
      </div>
      <div className="px-3 pb-3" data-tutorial="region-chronicles">
        {groups.map(({ eraLabel, chronicles: eraChronicles }, gi) => (
          <div key={eraLabel} className={gi === 0 ? "" : "mt-3"}>
            {/* Era header — only render when there's more than one
                era group. Single-era regions don't need the noise. */}
            {groups.length > 1 && (
              <div className="flex items-center gap-2 px-2 mb-1.5">
                <span
                  className="text-[9px] font-display uppercase tracking-[0.22em] whitespace-nowrap"
                  style={{ color: "hsl(var(--gold-dim) / 0.7)" }}
                >
                  {eraLabel}
                </span>
                <div className="flex-1 h-px" style={{ background: "hsl(var(--gold-dim) / 0.2)" }} />
              </div>
            )}
            <div className="space-y-0.5">
              {eraChronicles.map((chronicle) => {
                const rule = getUnlockRule(chronicle.id);
                const locked = rule && !isChronicleUnlocked(chronicle.id);
                const isCompleted = completedChronicles.includes(chronicle.id);
                const isPlayable = chronicle.available || (rule && !locked);
                const inProgress = !locked && isPlayable && chronicleHasInProgressSave(chronicle.id);
                const endings = isCompleted ? chronicleEndingsProgress(chronicle.id) : { reached: 0, total: 0 };
                const showEndingsChip = isCompleted && endings.total > 0 && endings.reached < endings.total;
                return (
                  <button
                    data-tutorial={chronicle.id === "carolingian-empire" ? "chronicle-carolingian-empire" : undefined}
                    key={chronicle.id}
                    onClick={() => { if (!locked) { navigate(`/chronicle/${chronicle.id}`); onClose(); } }}
                    className={`w-full text-left px-2.5 py-1.5 sm:py-1 rounded-sm transition-all duration-200 group/item ${locked ? "cursor-default opacity-60" : "hover:bg-secondary hover:shadow-[inset_0_0_12px_hsl(var(--gold)/0.06)] active:bg-secondary/80"}`}
                  >
                    <div className="flex items-center gap-2">
                      <h4 className={`font-display text-[11px] tracking-wide flex-1 ${locked ? "text-muted-foreground/60" : "text-foreground/90 group-hover/item:text-primary transition-colors"}`}>
                        {chronicle.title}
                      </h4>
                      {locked && <Lock className="w-3 h-3 text-muted-foreground/40 flex-shrink-0" />}
                      {!locked && isCompleted && (
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Check className="w-3 h-3 flex-shrink-0" style={{ color: "hsl(var(--gold-dim))" }} />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-[10px] font-display tracking-wider">
                              Chronicle Completed
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <p className="text-[9px] text-muted-foreground mt-0.5 font-display tracking-wider">
                      {chronicle.dateRange[0]} – {chronicle.dateRange[1]}
                    </p>
                    {locked ? (
                      <p className="text-[10px] text-muted-foreground/40 mt-0.5 font-body italic leading-snug">{rule.lockedHint}</p>
                    ) : (
                      <>
                        <p className="text-[10px] text-foreground/40 mt-0.5 line-clamp-1 font-body leading-snug">{chronicle.description}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">

                          <span className={`inline-flex items-center gap-1 text-[9px] font-display uppercase tracking-[0.15em] ${isPlayable ? "text-primary/70" : "text-muted-foreground/40"}`}>
                            {isPlayable ? "→ Play" : "Coming Soon"}
                          </span>
                          {/* Continue pill — in-progress chronicles
                              get a small chevron-right marker so the
                              player can resume straight from the
                              region dropdown. */}
                          {inProgress && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-display uppercase tracking-[0.18em]"
                              style={{
                                background: "hsl(var(--primary) / 0.12)",
                                border: "1px solid hsl(var(--primary) / 0.35)",
                                color: "hsl(var(--primary))",
                              }}
                            >
                              <ChevronRight className="w-2.5 h-2.5" />
                              Continue
                            </span>
                          )}
                          {/* Endings-remaining chip — only shown for
                              completed chronicles that still have
                              unseen endings. Players hunting endings
                              can scan a region in two seconds. */}
                          {showEndingsChip && (
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-display uppercase tracking-[0.18em]"
                                    style={{
                                      background: "hsl(var(--gold) / 0.08)",
                                      border: "1px solid hsl(var(--gold-dim) / 0.5)",
                                      color: "hsl(var(--gold-dim))",
                                    }}
                                  >
                                    {endings.reached} / {endings.total} endings
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-[10px] font-display tracking-wider">
                                  Replay to discover the {endings.total - endings.reached} remaining ending{endings.total - endings.reached === 1 ? "" : "s"}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {/* Chronicle-chain chips (Layer 1 — soft
                              coupling). Renders one chip per
                              CHRONICLE_CHAINS entry the chronicle
                              belongs to, each with a "(N of K)"
                              position marker and a tooltip naming
                              the arc + a dynamic "Coming: X, Y" line
                              for unbuilt members. Most chronicles
                              show 0 or 1 chip; world-war-one shows
                              two (Great War & Tsars + Two World
                              Wars). */}
                          {getChronicleChains(chronicle.id).map(({ chain, position }) => {
                            const unbuilt = unbuiltMembersOfChain(chain);
                            return (
                              <TooltipProvider key={chain.label} delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-display uppercase tracking-[0.18em]"
                                      style={{
                                        background: "hsl(0, 30%, 40% / 0.12)",
                                        border: "1px solid hsl(0, 32%, 45% / 0.45)",
                                        color: "hsl(0, 32%, 60%)",
                                      }}
                                    >
                                      {chain.label} ({position} of {chain.chronicleIds.length})
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[280px] text-[10px] font-display tracking-wider leading-relaxed">
                                    {chain.tooltip}
                                    {unbuilt.length > 0 && (
                                      <div
                                        className="mt-1.5 pt-1.5 border-t border-border/30"
                                        style={{ color: "hsl(var(--muted-foreground) / 0.85)" }}
                                      >
                                        Coming: {unbuilt.join(", ")}
                                      </div>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function RegionMarkerImpl({ regionId, regionName, chronicles, onHoverChange }: RegionMarkerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [completedChronicles, setCompletedChronicles] = useState<string[]>([]);
  // Lock <body> scroll only when the mobile bottom-sheet is open. The
  // desktop dropdown is a small absolutely-positioned panel that doesn't
  // overlay the whole viewport, so the page underneath should still be
  // interactive.
  useBodyScrollLock(isOpen && isMobile);

  // Load completion state on mount so the count badge and unread-pulse
  // can react before the player opens the panel. Re-running on isOpen
  // would be too late — the marker needs the data immediately to render
  // the right badge tint and decide whether to pulse.
  useEffect(() => {
    try { setCompletedChronicles(JSON.parse(localStorage.getItem("chronicles-completed") || "[]")); } catch { setCompletedChronicles([]); }
  }, []);

  // Close on outside click (desktop)
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handler = (e: MouseEvent) => {
      if (isWalkthroughEvent(e)) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, isMobile]);

  const hasChronicles = chronicles.length > 0;
  const hasPlayable = chronicles.some((c) => c.available || getUnlockRule(c.id));

  // Per-region progress badge — shows "completed / total" where
  // `total` is every chronicle catalogued for the region, including
  // ones still marked coming-soon. Reads as "you've finished N out
  // of the M chronicles this region will eventually have". The
  // numerator counts only chronicles the player has actually
  // completed, so as new content ships the denominator stays put
  // (no surprise jumps) and the player's progress count only
  // increases.
  //
  // `isRegionComplete` still drives the celebration pulse + laurels.
  // It compares completed-count to currently-playable chronicles
  // (not the total) — otherwise regions with coming-soon entries
  // could never trigger the celebration, which would feel punitive.
  // The badge text and the celebration use the same numerator but
  // different denominators on purpose.
  const { completedCount, totalCount, isRegionComplete, isRegionCompletionist } = useMemo(() => {
    let playable = 0;
    let completed = 0;
    for (const c of chronicles) {
      const rule = getUnlockRule(c.id);
      const isPlayable = c.available || (rule != null && isChronicleUnlocked(c.id));
      if (isPlayable) {
        playable++;
        if (completedChronicles.includes(c.id)) completed++;
      }
    }
    const regionComplete = playable > 0 && completed === playable;
    // Completionist tier: every memory flag for every playable
    // chronicle in the region must also be unlocked. Drives the
    // shield's full-gold + shimmer treatment. Computed alongside
    // regionComplete so the shield only ever upgrades from gilded
    // (region complete) to gilded+shimmer (region completionist).
    let allFlagsFound = regionComplete;
    if (regionComplete) {
      for (const c of chronicles) {
        const rule = getUnlockRule(c.id);
        const isPlayable = c.available || (rule != null && isChronicleUnlocked(c.id));
        if (!isPlayable) continue;
        const defined = chronicleMemoryFlagIds[c.id] || [];
        if (defined.length === 0) continue;
        const unlocked = new Set(getUnlockedFlags(c.id));
        if (!defined.every((id) => unlocked.has(id))) {
          allFlagsFound = false;
          break;
        }
      }
    }
    return {
      completedCount: completed,
      totalCount: chronicles.length,
      playableCount: playable,
      isRegionComplete: regionComplete,
      isRegionCompletionist: regionComplete && allFlagsFound,
    };
  }, [chronicles, completedChronicles]);

  // The parent (EuropeMap) now positions the marker in screen-pixel
  // space *outside* the zoomed map wrapper, so shields stay sharp at
  // every zoom level (no GPU bitmap-stretching of a transformed
  // compositor layer). Our job is just to center ourselves on the
  // anchor point the parent provided.
  //
  // `relative` is required for the `z-[80]` / `z-30` / `z-20` classes
  // below to take effect — z-index is ignored on `position: static`
  // elements. Without it the open shield's popup paints at z-auto
  // and sibling shields render on top of it (and the cluster of
  // shields in the central Med fights itself for click targets by
  // DOM order). Local hovered shields elevate to z-30 so clicks
  // always land on the marker the player is actively pointing at.
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    const openForTutorial = (event: Event) => {
      if (regionId !== "hre") return;
      setIsOpen((event as CustomEvent<string>).detail === "region");
    };
    window.addEventListener(TUTORIAL_HOME_VIEW_EVENT, openForTutorial);
    return () => window.removeEventListener(TUTORIAL_HOME_VIEW_EVENT, openForTutorial);
  }, [regionId]);
  const zClass = isOpen && !isMobile ? "z-[80]" : isHovered ? "z-30" : "z-20";
  return (
    <>
      <div
        ref={panelRef}
        className={`relative group/region ${zClass}`}
        style={{
          transform: "translate(-50%, -50%)",
          transformOrigin: "center center",
        }}
        onPointerEnter={() => { setIsHovered(true); onHoverChange?.(regionId); }}
        onPointerLeave={() => { setIsHovered(false); onHoverChange?.(null); }}
      >
        {/* Marker button */}
        <motion.button
          data-tutorial={regionId === "hre" ? "region-hre" : regionId === "lowCountries" ? "region-low-countries" : regionId === "france" ? "region-france" : undefined}
          onClick={() => hasChronicles && setIsOpen(!isOpen)}
          whileHover={{ scale: 1.15, y: -2 }}
          whileTap={{ scale: 0.95 }}
          // min-w-[44px] floors the marker's tap target at the 44px WCAG
          // minimum. Short-label regions (e.g. "Italy") were ~41px wide;
          // wider labels already exceed 44 so this only affects the narrow
          // ones, and the shield + label stay centered (items-center).
          className="relative flex flex-col items-center group min-w-[44px]"
          style={{ padding: "4px" }}
        >
          {/* Shield wrapper — relative-positioned so the count badge
              and completion pulse sit relative to the shield itself,
              not the wider motion.button (which also contains the
              region label below). When the region is complete the
              shield itself becomes "gilded" (thicker, brighter gold
              stroke + soft gold halo) instead of wearing an extra
              ornament — heraldry's own way of honouring an escutcheon
              without piling decoration on top of it. */}
          <div className="relative">
            {/* Multi-layered drop shadow so the shield reads as a physical
                object standing on the map: a sharp contact shadow at the
                base, a diffused mid-shadow for the body, and a long ambient
                cast that suggests overhead light. The combined filter is
                what tells the eye "this pin is sitting on the map" rather
                than "this pin is part of the map." Complete regions add
                an extra outer gold glow so the gilded shield is legible
                on dark map terrain. */}
            <svg viewBox="0 0 40 48" className={`w-7 h-[34px] transition-all duration-300 block ${!hasPlayable ? "opacity-40" : ""}`} style={{
              filter: !hasPlayable
                ? 'drop-shadow(0 1px 1px hsl(0 0% 0% / 0.5)) drop-shadow(0 3px 5px hsl(0 0% 0% / 0.3))'
                : isRegionCompletionist
                  ? 'drop-shadow(0 0 6px hsl(43 95% 70% / 0.7)) drop-shadow(0 0 12px hsl(43 90% 65% / 0.4)) drop-shadow(0 1px 1px hsl(0 0% 0% / 0.7)) drop-shadow(0 4px 6px hsl(0 0% 0% / 0.5)) drop-shadow(0 10px 18px hsl(0 0% 0% / 0.35))'
                  : isRegionComplete
                    ? 'drop-shadow(0 0 4px hsl(43 80% 60% / 0.55)) drop-shadow(0 1px 1px hsl(0 0% 0% / 0.7)) drop-shadow(0 4px 6px hsl(0 0% 0% / 0.5)) drop-shadow(0 10px 18px hsl(0 0% 0% / 0.35))'
                    : 'drop-shadow(0 1px 1px hsl(0 0% 0% / 0.7)) drop-shadow(0 4px 6px hsl(0 0% 0% / 0.5)) drop-shadow(0 10px 18px hsl(0 0% 0% / 0.35))',
            }}>
              {/* SVG <defs>: a vertical gold gradient used as the shield
                  fill for the completionist tier. Replaces the dark ink
                  fill so the shield reads as solid polished gold rather
                  than the gilded-edge-on-dark of the regular complete
                  state. */}
              {isRegionCompletionist && (
                <defs>
                  <linearGradient id={`shield-gold-${regionId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="hsl(45, 92%, 70%)" />
                    <stop offset="55%"  stopColor="hsl(43, 80%, 50%)" />
                    <stop offset="100%" stopColor="hsl(36, 70%, 35%)" />
                  </linearGradient>
                </defs>
              )}
              <path
                d="M 20,2 L 36,10 L 36,28 Q 36,40 20,46 Q 4,40 4,28 L 4,10 Z"
                fill={isRegionCompletionist ? `url(#shield-gold-${regionId})` : "hsl(var(--ink))"}
                stroke={isRegionCompletionist ? "hsl(43, 95%, 75%)" : isRegionComplete ? "hsl(43, 82%, 62%)" : "hsl(var(--gold))"}
                strokeWidth={isRegionCompletionist ? 2.6 : isRegionComplete ? 2.6 : 1.5}
              />
              {/* Gilded inner highlight — present on both the gilded
                  and the completionist tiers. A thinner inset stroke
                  that catches "twice-reflected" light along the engraved
                  inner edge. */}
              {(isRegionComplete || isRegionCompletionist) && (
                <path
                  d="M 20,4.6 L 33.6,11.4 L 33.6,27.6 Q 33.6,38.6 20,43.4 Q 6.4,38.6 6.4,27.6 L 6.4,11.4 Z"
                  fill="none"
                  stroke={isRegionCompletionist ? "hsl(48, 100%, 90%)" : "hsl(43, 90%, 78%)"}
                  strokeWidth={isRegionCompletionist ? 0.8 : 0.6}
                  opacity="0.9"
                />
              )}
              <text x="20" y="26" textAnchor="middle" fontSize="16" className="select-none" fill={regionId === "scandinavia" ? (isRegionCompletionist ? "hsl(28, 30%, 12%)" : "white") : isRegionCompletionist ? "hsl(28, 30%, 12%)" : undefined}>
                {REGION_ICONS[regionId] || "📍"}
              </text>
            </svg>

            {/* Subtle shimmer — diagonal sheen sweeping across the
                full-gold shield every ~5 s. Shaped to the heraldic
                outline via a CSS mask so it tracks the escutcheon
                rather than stamping a rectangle. Shown only on the
                completionist tier; the gilded-but-not-yet-completionist
                state stays still. */}
            {isRegionCompletionist && (
              <span aria-hidden="true" className="shield-completionist-shimmer w-7 h-[34px] absolute top-0 left-0" />
            )}

            {/* Default pulse — slow sonar ring expanding from the shield,
                fires on every region that has playable chronicles the
                player hasn't yet completed. Reads as an ambient "this
                region still has work to do" signal that draws the eye
                toward unfinished content. Stops when the region is
                complete — at that point the laurel-wreath flanks
                become the resting state and the pulse would just be
                noise. Suppressed while the dropdown is open so it
                doesn't fight the panel's animation. */}
            {hasPlayable && !isRegionComplete && !isOpen && (
              <motion.div
                aria-hidden="true"
                className="absolute inset-0 rounded-md pointer-events-none"
                animate={{
                  boxShadow: [
                    "0 0 0 0 hsl(var(--gold) / 0.55)",
                    "0 0 0 14px hsl(var(--gold) / 0)",
                  ],
                }}
                transition={{ duration: 3.0, repeat: Infinity, ease: "easeOut" }}
              />
            )}

            {/* Progress badge — small brass pill at the shield's top-
                right corner showing "completed / total" where total
                counts every chronicle catalogued for the region,
                released or coming-soon. Hidden when the region has
                no chronicles at all (nothing to track).
                Tint:
                  - 0 completed         → muted (no progress yet)
                  - region complete     → full gold (matches pulse)
                  - partial progress    → gold-dim (some done, more to go) */}
            {totalCount > 0 && (
              <span
                aria-label={`${completedCount} of ${totalCount} chronicles completed`}
                className="absolute -top-1 -right-2 z-[5] min-w-[22px] h-[15px] px-1 rounded-sm flex items-center justify-center pointer-events-none select-none"
                style={{
                  background: "linear-gradient(180deg, hsl(35, 35%, 26%), hsl(28, 25%, 16%))",
                  border: "1px solid hsl(43, 50%, 35% / 0.6)",
                  boxShadow: "0 1px 3px hsl(0 0% 0% / 0.55)",
                }}
              >
                <span
                  className="font-display tracking-tight leading-none"
                  style={{
                    fontSize: "8.5px",
                    color: completedCount === 0
                      ? "hsl(var(--muted-foreground) / 0.85)"
                      : isRegionComplete
                        ? "hsl(var(--gold))"
                        : "hsl(var(--gold-dim))",
                  }}
                >
                  {completedCount}/{totalCount}
                </span>
              </span>
            )}
          </div>
          {/* The default group-hover halo was removed — its job (signalling
              "there's work to do here") is now done by the always-on pulse
              while the region has incomplete playable chronicles. Mouse
              feedback comes from the existing whileHover scale + lift on
              the motion.button itself. */}
          {/* Hover feedback is handled by the motion.button whileHover scale + lift. */}
          <span
            className={`mt-1 text-[10px] sm:text-[11px] font-display uppercase tracking-[0.1em] whitespace-nowrap transition-all duration-300 ${
              hasPlayable ? "text-gold group-hover:text-gold" : "text-muted-foreground/50"
            }`}
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.95), 0 0 8px rgba(0,0,0,0.7), 0 0 2px rgba(0,0,0,0.9)" }}
          >
            {regionName}
          </span>
          {/* Completion is now signalled entirely by the gilded shield
              (thicker, brighter gold stroke + soft gold halo + inner
              highlight) — no separate ornament sits under the name. */}
        </motion.button>

        {/* Desktop dropdown */}
        <AnimatePresence>
          {isOpen && !isMobile && hasChronicles && (
            <DesktopPanel
              regionId={regionId}
              regionName={regionName}
              chronicles={chronicles}
              panelRef={panelRef}
              dropdownRef={dropdownRef}
              onClose={() => setIsOpen(false)}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Mobile: full bottom sheet rendered through a React portal at
          document.body so its `position: fixed` is anchored to the
          viewport, NOT to the EuropeMap's transformed pan-wrapper.
          Without the portal, fixed-positioned descendants of a parent
          with `transform` get re-anchored to that parent (browser
          quirk), which made the sheet inherit the panned map's width
          — much wider than the actual viewport on phones. */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {isOpen && isMobile && hasChronicles && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[90] bg-black/60"
                onClick={() => setIsOpen(false)}
                style={{ touchAction: "none" }}
              />
              {/* Bottom sheet */}
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                className="fixed bottom-0 left-0 right-0 z-[95] rounded-t-lg overflow-hidden pb-safe flex flex-col"
                style={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderBottom: "none",
                  maxHeight: "80vh",
                }}
                // Defensive: even though this sheet is portaled to
                // document.body (so touch events shouldn't bubble to
                // the map's container anyway), swallow them here so
                // there is zero chance of the map underneath reacting
                // to finger scrolling on the chronicle list.
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1">
                  <div className="w-10 h-1 rounded-full bg-border/60" />
                </div>
                <div className="h-[1px] mx-0" style={{ background: "hsl(var(--gold-dim) / 0.2)" }} />
                {/* flex-1 + min-h-0 lets the list shrink within the 80vh
                    sheet so the Close footer below ALWAYS stays on-screen,
                    no matter how long the chronicle list is. (Previously a
                    fixed max-height let a long list push the footer past the
                    sheet's clipped bottom edge — the cause of "Close not
                    visible in all regions" on mobile.) */}
                <div
                  className="overflow-y-auto flex-1 min-h-0"
                  style={{ touchAction: "pan-y" }}
                >
                  <PanelContent
                    regionId={regionId}
                    regionName={regionName}
                    chronicles={chronicles}
                    completedChronicles={completedChronicles}
                    onClose={() => setIsOpen(false)}
                  />
                </div>
                {/* Close button */}
                <div className="px-4 pb-4 pt-2 border-t border-border/20">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-full py-3 font-display text-[11px] uppercase tracking-[0.2em] text-muted-foreground border border-border/40 rounded-sm hover:text-foreground transition-colors"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

// Memoize the shield marker so it doesn't re-render every time its parent
// (EuropeMap → Index) re-renders for unrelated reasons. The most common
// trigger is the `hoveredRegion` state in Index.tsx changing on every
// pointer-enter/leave — without memo this cascades through 13 shields and
// every shield's framer-motion `repeat: Infinity` pulse reconciles, which
// shows up as visible animation jank. Props are referentially stable:
// `regionId` / `regionName` are constants per shield, `chronicles` comes
// from a useMemo'd `chroniclesByRegion` (the `|| []` fallback in EuropeMap
// was replaced by a stable EMPTY_CHRONICLES constant for the same reason),
// and `onHoverChange` is React's setState setter which never changes
// identity. Default shallow compare is enough.
const RegionMarker = memo(RegionMarkerImpl);
export default RegionMarker;
